import { AUTH_API_BASE } from '../config/authApiBase';
import {
  getAuthHeader,
  isAccessTokenError,
  refreshAuthTokens,
} from './authTokenStore';

const API_BASE = AUTH_API_BASE;

const SESSION_EXPIRED_MESSAGE = 'Сессия истекла. Войдите снова.';

export type ApiJsonResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string };

type FetchOptions = {
  body?: unknown;
  withAuth?: boolean;
};

type RawFetchResult = {
  response: Response;
  text: string;
  parsed: unknown;
};

function parseResponseText(text: string): unknown {
  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch {
    return {};
  }
}

function extractErrorMessage(status: number, parsed: unknown): string {
  const o = parsed as { error?: string; message?: string };
  if (typeof o?.error === 'string' && o.error.trim()) return o.error;
  if (typeof o?.message === 'string' && o.message.trim()) return o.message;
  return `Запрос не выполнен (${status})`;
}

function buildHeaders(withAuth: boolean): Record<string, string> | null {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (!withAuth) return headers;

  const auth = getAuthHeader();
  if (!auth) return null;
  Object.assign(headers, auth);
  return headers;
}

function shouldRetryAuth(path: string, withAuth: boolean): boolean {
  if (!withAuth) return false;
  if (path === '/auth/refresh' || path.startsWith('/auth/login') || path.startsWith('/auth/register')) {
    return false;
  }
  return true;
}

async function performFetch(
  method: string,
  path: string,
  options: FetchOptions,
  allowAuthRetry: boolean,
): Promise<RawFetchResult> {
  const withAuth = options.withAuth !== false;
  const headers = buildHeaders(withAuth);
  if (withAuth && !headers) {
    return {
      response: new Response(null, { status: 401 }),
      text: '',
      parsed: {},
    };
  }

  const response = await fetch(`${API_BASE}${path}`, {
    method,
    headers: headers || undefined,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  const text = await response.text();
  const parsed = parseResponseText(text);
  const status = response.status;

  if (
    allowAuthRetry
    && shouldRetryAuth(path, withAuth)
    && status === 401
    && isAccessTokenError(status, extractErrorMessage(status, parsed))
  ) {
    const refreshed = await refreshAuthTokens();
    if (refreshed) {
      return performFetch(method, path, options, false);
    }
  }

  return { response, text, parsed };
}

/** GET/POST с разбором тела ответа, авто-refresh при 401 и текстом ошибки. */
export async function apiFetchJson<T>(
  method: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  options?: FetchOptions,
): Promise<ApiJsonResult<T>> {
  const withAuth = options?.withAuth !== false;
  const headers = buildHeaders(withAuth);
  if (withAuth && !headers) {
    return { ok: false, status: 401, error: 'Требуется вход в аккаунт' };
  }

  const { response, parsed } = await performFetch(method, path, options ?? {}, true);
  const status = response.status;

  if (!response.ok) {
    let errMsg = extractErrorMessage(status, parsed);
    if (status === 401 && withAuth && isAccessTokenError(status, errMsg)) {
      errMsg = SESSION_EXPIRED_MESSAGE;
    }
    return { ok: false, status, error: errMsg };
  }

  return { ok: true, status, data: parsed as T };
}

export async function apiGet<T>(path: string, withAuth = true): Promise<T | null> {
  const { response, parsed } = await performFetch('GET', path, { withAuth }, true);
  if (!response.ok) return null;
  return parsed as T;
}

export async function apiPost<T>(path: string, body: unknown, withAuth = true): Promise<T | null> {
  const { response, parsed } = await performFetch('POST', path, { body, withAuth }, true);
  if (!response.ok) return null;
  return parsed as T;
}
